import { ApolloClient, NormalizedCacheObject } from "@apollo/client";
import { AgGrid } from "@jsq/ds/core";
import { client } from "~/apollo/client";
import { GetFilteredEntitiesDocument } from "~/graphql/supergraphHooks";
import { type EntitiesFilters, type GetFilteredEntitiesQuery } from "~/graphql/supergraphTypes";
import { ComplianceCaseSort } from "~/pages/Compliance/ComplianceTable/types";
import { InvestmentEntityRows, ProgramsType, RowItem } from "./types";

const getFilterVars = (
  filterModel: AgGrid.FilterModel | AgGrid.AdvancedFilterModel | null,
): EntitiesFilters | null => {
  if (!filterModel) {
    return null;
  }
  if (filterModel?.filterType) {
    throw Error("Handling for AgGrid.AdvancedFilterModel not implemented.");
  }
  const queryFilters: EntitiesFilters = {};
  Object.entries(filterModel ?? {}).forEach((filter) => {
    const [field, filterInfo] = filter;
    if (filterInfo.filterType == "text" && field === "name") {
      queryFilters["nameSearchTerm"] = filterInfo.filter;
    } else if (filterInfo.filterType == "text" && field === "arenaName") {
      queryFilters["arenaSearchTerm"] = filterInfo.filter;
    } else if (filterInfo.filterType == "set" && field === "complianceProgram") {
      queryFilters["compliancePrograms"] = filterInfo.values.map((v: string) =>
        v == "--" ? null : v,
      );
    } else if (filterInfo.filterType == "set" && field === "complianceEnabled") {
      queryFilters["complianceEnabled"] = filterInfo.values.map((v: string) =>
        v == "No compliance program assigned" ? null : v == "Enabled",
      );
    } else {
      // Would need to change this if we add other filter types
      throw Error(`Unsupported filter type ${field}`);
    }
  });
  return queryFilters;
};

export const rowsFromQuery = (
  investmentEntities: InvestmentEntityRows | [],
  allPrograms: ProgramsType,
) => {
  const rowData: RowItem[] =
    investmentEntities.map((investmentEntity) => {
      const program = allPrograms?.find(
        (program) => program.name === investmentEntity?.complianceSettings?.complianceProgram,
      );
      return {
        id: investmentEntity?.id,
        arenaId: investmentEntity?.arenaId,
        name: investmentEntity?.name || "",
        arenaName: investmentEntity?.arena?.name || "",
        complianceProgram: program?.name || "--",
        complianceEnabled: !program?.name
          ? "No compliance program assigned"
          : investmentEntity.complianceSettings?.enabled
            ? "Enabled"
            : "Disabled",
        isInvestorDiligenceEnabled: investmentEntity.isInvestorDiligenceEnabled
          ? "Legacy KYC Enabled"
          : "No",
      };
    }) ?? [];
  return rowData;
};

const getSortVars = (sortItems: AgGrid.SortModelItem[]): ComplianceCaseSort[] | null => {
  if (!sortItems) {
    return null;
  }
  const querySorts: ComplianceCaseSort[] = [];
  sortItems.forEach((sort) => {
    querySorts.push({ field: sort.colId, sortAscending: sort.sort === "asc" });
  });
  return querySorts;
};

export class InvestmentEntitiesGraphqlDataSource implements AgGrid.IServerSideDatasource {
  client: ApolloClient<NormalizedCacheObject>;
  allPrograms: ProgramsType; // Adjust the type as needed for `allPrograms`.
  userScopedArenaIds: string[] | null;
  constructor(allPrograms: ProgramsType, userScopedArenaIds: string[] | null = null) {
    this.client = client;
    this.allPrograms = allPrograms;
    this.userScopedArenaIds = userScopedArenaIds;
  }

  private applyArenaScope(filters: EntitiesFilters | null): EntitiesFilters | null {
    if (this.userScopedArenaIds === null) {
      return filters;
    }
    return { ...filters, arenaIds: this.userScopedArenaIds };
  }

  public getRows(params: AgGrid.IServerSideGetRowsParams): void {
    if (this.userScopedArenaIds !== null && this.userScopedArenaIds.length === 0) {
      params.success({ rowData: [], rowCount: 0 });
      return;
    }

    const offset = params.request.startRow ?? 0;
    const limit = params.request.endRow ?? 0 - (params.request.startRow ?? 0);
    const filters = this.applyArenaScope(getFilterVars(params.request.filterModel));
    const sorts = getSortVars(params.request.sortModel);

    this.client
      .query<GetFilteredEntitiesQuery>({
        query: GetFilteredEntitiesDocument,
        variables: {
          filters,
          sorts,
          offset,
          limit,
        },

        fetchPolicy: "no-cache",
      })
      .then((response) => {
        params.success({
          rowData: rowsFromQuery(
            response.data.investmentEntitiesForProgramAssignment?.rows ?? [],
            this.allPrograms,
          ),
          rowCount: response.data.investmentEntitiesForProgramAssignment?.filteredCount,
        });
      })
      .catch(() => {
        params.fail();
      });
  }

  public async getAllFilteredRows(
    filterModel: AgGrid.FilterModel | null,
    sortModel: AgGrid.SortModelItem[],
  ): Promise<RowItem[]> {
    if (this.userScopedArenaIds !== null && this.userScopedArenaIds.length === 0) {
      return [];
    }

    const filters = this.applyArenaScope(getFilterVars(filterModel));
    const sorts = getSortVars(sortModel);

    const response = await this.client.query<GetFilteredEntitiesQuery>({
      query: GetFilteredEntitiesDocument,
      variables: {
        filters,
        sorts,
        offset: 0,
        // Set a high limit to fetch all filtered rows for CSV export.
        // The backend query is already filtered and typically returns fewer than 10k rows.
        // This avoids pagination while exporting all filtered data.
        limit: 10000,
      },

      fetchPolicy: "no-cache",
    });

    return rowsFromQuery(
      response.data.investmentEntitiesForProgramAssignment?.rows ?? [],
      this.allPrograms,
    );
  }
}
